import { useCallback, useEffect, useState } from 'react';

/**
 * EL TEMA
 * =======
 *
 * Por defecto manda el sistema (`prefers-color-scheme`), que ya está resuelto en la capa de
 * tokens. La persona puede forzar claro u oscuro y eso se recuerda: el que atiende el
 * mostrador de noche no tiene que volver a elegirlo cada mañana.
 *
 * Tres estados, no dos: "sistema" es distinto de "claro". Si solo hubiera dos, elegir claro
 * una vez desengancharía para siempre la preferencia del sistema.
 */
export type Tema = 'sistema' | 'claro' | 'oscuro';

const CLAVE = 'planilla:tema';

function leer(): Tema {
  try {
    const guardado = localStorage.getItem(CLAVE);
    return guardado === 'claro' || guardado === 'oscuro' ? guardado : 'sistema';
  } catch {
    return 'sistema';
  }
}

function aplicar(tema: Tema): void {
  // Los tokens leen data-theme; sin atributo, gobierna prefers-color-scheme.
  if (tema === 'sistema') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', tema);
}

/** Qué se está viendo ahora mismo, ya resuelto el "sistema". */
export function temaEfectivo(tema: Tema): 'claro' | 'oscuro' {
  if (tema !== 'sistema') return tema;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'oscuro' : 'claro';
  } catch {
    return 'claro';
  }
}

export function useTema(): { tema: Tema; efectivo: 'claro' | 'oscuro'; cambiar: (tema: Tema) => void; alternar: () => void } {
  const [tema, setTema] = useState<Tema>(leer);
  const [delSistema, setDelSistema] = useState<'claro' | 'oscuro'>(() => temaEfectivo('sistema'));

  // Si el sistema cambia a oscuro a las siete de la tarde, la pantalla acompaña.
  useEffect(() => {
    let consulta: MediaQueryList;
    try {
      consulta = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const escuchar = (evento: MediaQueryListEvent) => setDelSistema(evento.matches ? 'oscuro' : 'claro');
    consulta.addEventListener('change', escuchar);
    return () => consulta.removeEventListener('change', escuchar);
  }, []);

  const cambiar = useCallback((nuevo: Tema) => {
    setTema(nuevo);
    aplicar(nuevo);
    try {
      if (nuevo === 'sistema') localStorage.removeItem(CLAVE);
      else localStorage.setItem(CLAVE, nuevo);
    } catch {
      /* sin almacenamiento: el cambio vale para esta pestaña */
    }
  }, []);

  const efectivo = tema === 'sistema' ? delSistema : tema;
  const alternar = useCallback(() => cambiar(efectivo === 'oscuro' ? 'claro' : 'oscuro'), [cambiar, efectivo]);

  return { tema, efectivo, cambiar, alternar };
}

/**
 * El valor actual del token de marca, tal como lo ve el navegador. Sirve para que un selector
 * de color arranque en el color de la entidad sin escribir un hexadecimal a mano en el código
 * —que además lo rechazaría el verificador de tokens, y con razón—.
 */
export function colorDeMarcaActual(): string {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue('--marca-base').trim();
  } catch {
    return '';
  }
}
